import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Upload, Clock, AlertCircle, Brain, FileImage, Eye } from 'lucide-react';
import { DropZone, ResultsPanel, ExtractionsHistoryTab, API_URL } from '../components/ocr';

export const OCRPage = () => {
  const { t } = useTranslation('ocr');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [extractionResult, setExtractionResult] = useState(null);
  const [aiConfig, setAiConfig] = useState(null);
  const [showResultsPanel, setShowResultsPanel] = useState(false);
  const pollingIntervalRef = useRef(null);
  const currentExtractionIdRef = useRef(null);
  const pollingErrorCountRef = useRef(0);

  useEffect(() => {
    checkAiConfig();
    
    // Cleanup polling on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const checkAiConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/config/ai-provider`);
      setAiConfig(response.data);
    } catch (error) {
      console.error('Error checking AI config:', error);
    }
  };

  const pollExtractionStatus = useCallback(async (extractionId) => {
    // Skip if this is not the current extraction we're tracking
    if (extractionId !== currentExtractionIdRef.current) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/ocr/status/${extractionId}`);
      const status = response.data;
      
      // Reset error count on success
      pollingErrorCountRef.current = 0;
      
      setProcessingStatus(status);
      
      if (status.status === 'completed') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Set extraction result
        const extractionData = status.extraction || status.data;
        setExtractionResult({
          extraction_id: extractionId,
          data: extractionData?.extracted_data || status.data,
          filename: extractionData?.filename,
          provider: extractionData?.provider,
          model: extractionData?.model
        });
        setShowResultsPanel(true);
        setIsProcessing(false);
        toast.success(t('messages.processedSuccess'));
        
        // Clear status from server memory
        try {
          await axios.delete(`${API_URL}/api/ocr/status/${extractionId}`);
        } catch (e) {
          // Ignore cleanup errors
        }
      } else if (status.status === 'error') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        setIsProcessing(false);
        toast.error(status.message || t('messages.errorProcessing'));
        setExtractionResult({ error: status.message || status.error });
        setShowResultsPanel(true);
      }
    } catch (error) {
      // Increment error count
      pollingErrorCountRef.current += 1;
      
      // If we get 404 (not found), the extraction might have completed and been cleaned up
      // or something went wrong. After 5 consecutive errors, stop polling.
      if (error.response?.status === 404 || pollingErrorCountRef.current >= 5) {
        console.warn('Stopping polling after errors:', pollingErrorCountRef.current);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Only show error if we're still processing (not already completed)
        if (isProcessing && !extractionResult) {
          setIsProcessing(false);
          setProcessingStatus(null);
          // Don't show error toast for 404 - might just be cleanup
          if (error.response?.status !== 404) {
            toast.error(t('messages.errorProcessing'));
          }
        }
      }
      // For other errors, continue polling (network issues, etc.)
    }
  }, [t]);

  const handleFileSelect = async (file) => {
    setSelectedFile(file);
    setExtractionResult(null);
    setProcessingStatus({ status: 'queued', progress: 0, message: t('extract.dropzone.uploading') });
    setIsProcessing(true);

    // Clear any existing polling and reset error count
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingErrorCountRef.current = 0;

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Use async endpoint
      const response = await axios.post(`${API_URL}/api/ocr/extract-async`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const extractionId = response.data.extraction_id;
      currentExtractionIdRef.current = extractionId;
      
      setProcessingStatus({ 
        status: 'processing', 
        progress: 5, 
        message: t('extract.dropzone.processing') 
      });
      
      // Start polling for status every 1.5 seconds
      pollingIntervalRef.current = setInterval(() => {
        pollExtractionStatus(extractionId);
      }, 1500);
      
      // Initial poll
      pollExtractionStatus(extractionId);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorProcessing'));
      setExtractionResult({ error: error.response?.data?.detail });
      setShowResultsPanel(true);
      setIsProcessing(false);
      setProcessingStatus(null);
    }
  };

  const handleViewHistoryResult = (result, file) => {
    setExtractionResult(result);
    setSelectedFile(file);
    setShowResultsPanel(true);
  };

  const handleCreateOrder = async () => {
    if (!extractionResult?.extraction_id) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/ocr/extractions/${extractionResult.extraction_id}/create-order`
      );
      toast.success(t('messages.orderCreated', { orderId: response.data.order_id }));
      setShowResultsPanel(false);
      setSelectedFile(null);
      setExtractionResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorCreatingOrder'));
    }
  };

  return (
    <div className="space-y-6" data-testid="ocr-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('title')}</h1>
        <p className="text-muted-foreground mt-1">
          {t('subtitle')}
        </p>
      </div>

      {/* AI Config Status */}
      {!aiConfig?.is_active && (
        <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800 dark:text-yellow-200">{t('config.notConfigured')}</p>
                <p className="text-sm text-yellow-600 dark:text-yellow-300">
                  {t('config.notConfiguredHint')}
                </p>
              </div>
              <Button 
                variant="outline" 
                className="ml-auto"
                onClick={() => window.location.href = '/settings'}
              >
                {t('config.goToSettings')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Config Info */}
      {aiConfig?.is_active && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Brain className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">{t('config.configured')}</p>
                <p className="text-sm text-green-600 dark:text-green-300">
                  {t('config.provider')}: {aiConfig.provider?.toUpperCase()} | {t('config.model')}: {aiConfig.model}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="extract" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="extract" data-testid="extract-tab">
            <Upload className="h-4 w-4 mr-2" />
            {t('tabs.extract')}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">
            <Clock className="h-4 w-4 mr-2" />
            {t('tabs.history')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="extract">
          <Card>
            <CardHeader>
              <CardTitle>{t('extract.title')}</CardTitle>
              <CardDescription>
                {t('extract.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DropZone 
                onFileSelect={handleFileSelect} 
                isProcessing={isProcessing}
                processingStatus={processingStatus}
              />
              
              {/* Show processed file info */}
              {selectedFile && !isProcessing && extractionResult && (
                <div className="mt-4 flex items-center justify-between p-3 bg-muted rounded-lg border">
                  <div className="flex items-center gap-3">
                    <FileImage className="h-8 w-8 text-[#E63946]" />
                    <div>
                      <p className="font-medium">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(selectedFile.size / 1024).toFixed(1)} KB - {t('extract.processed')}
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setShowResultsPanel(true)}
                    className="bg-[#E63946] hover:bg-[#d32836]"
                    data-testid="view-results-btn"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {t('extract.viewResults')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>{t('history.title')}</CardTitle>
              <CardDescription>
                {t('history.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ExtractionsHistoryTab onViewResult={handleViewHistoryResult} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Results Side Panel */}
      <ResultsPanel
        isOpen={showResultsPanel}
        onClose={() => setShowResultsPanel(false)}
        result={extractionResult}
        selectedFile={selectedFile}
        onCreateOrder={handleCreateOrder}
      />
    </div>
  );
};

export default OCRPage;
